//Global imports
import { CCard, CCardBody, CCol, CFormLabel, CRow } from '@coreui/react';
import { cilPlus } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { use, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

//File imports
import { Language } from '../../../../language/English';
import Notification from '../../../../services/Notification/Notification';
import { DocumentTemplateTranslationService } from '../../Api/DocumentTemplateTranslationService';
import Logger from '../../../../services/Logger/Logger';
import { DocumentTemplateTranslationImageUploadPojo } from '../../Api/DocumentTemplateTranslationImageUploadPojo';
import {
    DOCUMENT_TEMPLATE_TRANSLATION_SUBFOLDER_TYPE_PREVIEW_IMAGES,
    UPLOAD_IMAGE_ALLOWED_FILE_EXTENSIONS,
} from '../../../../Constant';
import { appDispatch, RootState } from '../../../../app/store';
import {
    addPreviewImage,
    removePreviewImage,
    setPositionPreviewImage,
} from './Redux/previewImagesSlice';

//Component imports
import { HintText } from '../../../../components';
import { isArray } from 'util';

type UploadPreviewImagesProps = {
    titleHtmlFor: string;
    folderName: string | null;
    setLoader: (status: boolean) => void;
    setUniqueFolderName?: (name: string) => void;
    previewImageNoteState: boolean;
    setPreviewImageNoteState?: (status: boolean) => void;
};

const IMAGE_DIMENSION_LIMIT_THRESHOLD = 30;
const CARD_HEIGHT = '270px';

const UploadPreviewImages: React.FC<UploadPreviewImagesProps> = ({
    titleHtmlFor,
    folderName,
    setLoader = (status: boolean) => {},
    setUniqueFolderName = (name: string) => {},
    previewImageNoteState,
    setPreviewImageNoteState = (status: boolean) => {},
}) => {
    const notification = new Notification();
    const dispatch = useDispatch<appDispatch>();
    const documentTemplateService = new DocumentTemplateTranslationService();
    const logger = new Logger();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const previewImages = useSelector(
        (state: RootState) => state.previewImages.previewImages,
    );

    const validateImageDimensions = (file: File): Promise<boolean> => {
        return new Promise((resolve) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                const width = img.width;
                const height = img.height;

                URL.revokeObjectURL(objectUrl);

                const heightAllowed =
                    height >= 1047 - IMAGE_DIMENSION_LIMIT_THRESHOLD &&
                    height <= 1047 + IMAGE_DIMENSION_LIMIT_THRESHOLD;
                const widthAllowed =
                    width >= 720 - IMAGE_DIMENSION_LIMIT_THRESHOLD &&
                    width <= 720 + IMAGE_DIMENSION_LIMIT_THRESHOLD;

                resolve(heightAllowed && widthAllowed);
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(false);
            };

            img.src = objectUrl;
        });
    };

    const uploadImageToServer = async (file: File): Promise<void> => {
        try {
            setLoader(true);
            let payload = {
                folderName: folderName,
                type: DOCUMENT_TEMPLATE_TRANSLATION_SUBFOLDER_TYPE_PREVIEW_IMAGES,
                previewImage: file,
            };

            const { success, message, data } =
                await documentTemplateService.uploadImage(payload);

            const {
                uniqueFolderName,
                fileName,
                filePath,
                width,
                height,
                fileSizeInKb,
            }: DocumentTemplateTranslationImageUploadPojo = data;
            if (success) {
                setUniqueFolderName(uniqueFolderName);
                dispatch(
                    addPreviewImage({
                        image: fileName,
                        filePath,
                        width,
                        height,
                        fileSizeInKb,
                    }),
                );
                setPreviewImageNoteState(true);
            } else {
                notification.error(message ?? Language.UNABLE_TO_IMAGE);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setLoader(false);
        }
    };

    const handleImageChange = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ): Promise<void> => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (
            !fileExtension ||
            !UPLOAD_IMAGE_ALLOWED_FILE_EXTENSIONS.includes(fileExtension)
        ) {
            alert(Language.UPLOAD_IMAGE_FORMAT_VALIDATION);
            return;
        }

        const isValid = await validateImageDimensions(file);
        if (!isValid) {
            alert(Language.UPLOAD_IMAGE_DIMENSION_VALIDATION_MESSAGE);
            return;
        }

        await uploadImageToServer(file);

        e.target.value = '';
    };

    const handleUploadClick = () => {
        fileInputRef?.current?.click();
    };

    const moveImagePosition = (
        index: number,
        direction: 'left' | 'right',
    ): void => {
        const updatedArray = [...previewImages];
        const [removedItem] = updatedArray.splice(index, 1);
        const newIndex = direction === 'left' ? index - 1 : index + 1;
        updatedArray.splice(newIndex, 0, removedItem);
        dispatch(setPositionPreviewImage(updatedArray));
        setPreviewImageNoteState(true);
    };

    const deleteImage = (fileName: string) => {
        dispatch(removePreviewImage(fileName));
        setPreviewImageNoteState(true);
    };

    return (
        <>
            <div>
                <CFormLabel
                    style={{ marginBottom: 0 }}
                    htmlFor={titleHtmlFor}
                    className="mt-3"
                >
                    {Language.UPLOAD_PREVIEW_IMAGES}
                </CFormLabel>
                <HintText marginBottom="10px">
                    {Language.BLURRED_PREVIEW_IMAGE_HINT}
                </HintText>

                <CRow className="g-3">
                    {previewImages.map((img, index) => (
                        <CCol xs={12} sm={6} md={4} key={index}>
                            <CCard
                                className="border d-flex align-items-center justify-content-center"
                                style={{ height: CARD_HEIGHT }}
                            >
                                <img
                                    src={img.filePath}
                                    alt={`preview-${index}`}
                                    className="img-fluid"
                                    style={{
                                        maxHeight: '100%',
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                        setSelectedImage(img.filePath)
                                    }
                                />

                                <div className="d-flex gap-2 mt-2">
                                    {index > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() =>
                                                moveImagePosition(index, 'left')
                                            }
                                        >
                                            Left
                                        </button>
                                    )}

                                    {index < previewImages.length - 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() =>
                                                moveImagePosition(
                                                    index,
                                                    'right',
                                                )
                                            }
                                        >
                                            Right
                                        </button>
                                    )}
                                </div>
                                <div className="d-flex gap-2 mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => deleteImage(img.image)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </CCard>
                        </CCol>
                    ))}
                    <CCol xs={12} sm={6} md={4}>
                        <CCard
                            className="d-flex align-items-center justify-content-center border border-dashed"
                            style={{ height: CARD_HEIGHT, cursor: 'pointer' }}
                            onClick={handleUploadClick}
                        >
                            <CCardBody className="d-flex flex-column align-items-center justify-content-center">
                                <CIcon
                                    icon={cilPlus}
                                    size="xl"
                                    className="mb-2 text-primary"
                                />
                                <span className="text-primary fw-bold">
                                    Upload Image
                                </span>
                            </CCardBody>
                        </CCard>
                    </CCol>
                </CRow>

                {previewImageNoteState && (
                    <div className="alert alert-danger">
                        {Language.UPLOAD_PREVIEW_IMAGES_NOTE}
                    </div>
                )}

                {/* Hidden File Input */}
                <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                />
            </div>
            {selectedImage && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75"
                    style={{ zIndex: 1050 }}
                    onClick={() => setSelectedImage(null)}
                >
                    <div
                        className="position-relative bg-white rounded overflow-hidden d-flex align-items-center justify-content-center"
                        style={{ width: '90%', height: '90%' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={selectedImage}
                            alt="preview"
                            className="img-fluid"
                            style={{
                                height: '1047px',
                                width: '400px',
                                objectFit: 'contain',
                            }}
                        />
                        <button
                            type="button"
                            className="btn-close position-absolute top-0 end-0 m-2"
                            aria-label="Close"
                            onClick={() => setSelectedImage(null)}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default UploadPreviewImages;
